import React from 'react';
import { Lock, Crown, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import useCreatorPriceOptions from '../../hooks/useCreatorPriceOptions';
import { creditsToEur } from '../../lib/currency';
import { hasActiveCreatorProfile } from '../../lib/creatorPresentation';

export const MIN_TEASER = 10;
export const MAX_TEASER = 500;

/**
 * MonetizationPicker — Phase F6. Shared Gratis/A pagamento block for the
 * Racconti forms. Reuses the EXISTING admin price scales (scope) and the
 * Voce Pro gate. The backend re-validates everything (require_monetization).
 */
const MonetizationPicker = ({ scope, isPaid, setIsPaid, price, setPrice, teaser, setTeaser, disabled }) => {
  const { user } = useAuth();
  const { t } = useTranslation('common');
  const alreadyActive = hasActiveCreatorProfile(user);
  const { toast } = useToast();
  const navigate = useNavigate();
  const canMonetize = !!user?.is_verified_creator;
  const { credits } = useCreatorPriceOptions(scope);
  const paidOptions = credits.filter((c) => c > 0);

  React.useEffect(() => {
    const p = parseInt(price, 10);
    if (isPaid && paidOptions.length && !paidOptions.includes(p)) setPrice(String(paidOptions[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidOptions.length, isPaid]);

  return (
    <div className="rounded-lg border border-border bg-background p-3" data-testid="racconto-monetization">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
        <Sparkles className="h-3.5 w-3.5" /> Monetizzazione
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setIsPaid(false)}
          disabled={disabled}
          className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
            !isPaid ? 'border-black bg-black text-white' : 'border-border bg-card text-foreground hover:bg-secondary'
          }`}
          data-testid="racconto-free-btn"
        >
          Gratis
        </button>
        <button
          type="button"
          onClick={() => {
            if (!canMonetize) {
              toast({
                title: alreadyActive ? t('creator_presentation.unavailable_title') : 'Solo Voce Pro',
                description: alreadyActive ? t('creator_presentation.unavailable_description') : "Completa l'attivazione Voce Pro per vendere i tuoi Racconti.",
              });
              return;
            }
            setIsPaid(true);
          }}
          disabled={disabled}
          aria-disabled={!canMonetize}
          className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
            !canMonetize
              ? 'cursor-not-allowed border-border bg-secondary text-muted-foreground'
              : isPaid
                ? 'border-amber-600 bg-amber-500 text-white'
                : 'border-border bg-card text-foreground hover:bg-secondary'
          }`}
          data-testid="racconto-paid-btn"
        >
          {canMonetize ? 'A pagamento' : (
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" /> A pagamento
            </span>
          )}
        </button>
      </div>
      {!canMonetize && !alreadyActive && (
        <button
          type="button"
          onClick={() => navigate('/profile')}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold gold-text hover:bg-amber-500/20"
          data-testid="racconto-vip-cta"
        >
          <Crown className="h-3.5 w-3.5" /> Diventa Voce Pro per monetizzare i tuoi Racconti
        </button>
      )}
      {isPaid && canMonetize && (
        <div className="mt-3 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold">Prezzo (Crediti)</label>
            <select
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              disabled={disabled}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="racconto-price-select"
            >
              {paidOptions.map((c) => (
                <option key={c} value={c}>{c} Crediti · €{creditsToEur(c).toFixed(2)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold">
              <Lock className="h-3.5 w-3.5 gold-text" /> Anteprima pubblica <span className="gold-text">*</span>
            </label>
            <Textarea
              value={teaser}
              onChange={(e) => setTeaser(e.target.value.slice(0, MAX_TEASER))}
              placeholder="Racconta in poche righe cosa sbloccherà chi acquista…"
              className="min-h-[70px] resize-none border-border bg-background text-sm"
              maxLength={MAX_TEASER}
              disabled={disabled}
              data-testid="racconto-teaser-input"
            />
            <div className="mt-1 flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">Minimo {MIN_TEASER} caratteri, visibile a tutti.</span>
              <span className={`tabular-nums ${teaser.length < MIN_TEASER ? 'text-red-400' : 'text-muted-foreground'}`}>
                {teaser.length} / {MAX_TEASER}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonetizationPicker;
